/**
 * Column-overlay edge trim math.
 *
 * skip_prefix_words / cut_trailing_words count how many words are hidden at the
 * start / end of a column's first / last visible verse. The editor reveals a word
 * (delta -1) or hides one (delta +1); the result is clamped to [0, max] where max
 * keeps at least one word of the labeled verse visible. The backend independently
 * clamps to >= 0, so this is the source of truth for the UI's upper bound.
 */
export function adjustedTrim(current: number, delta: number, max: number): number {
  // Outer Math.max(0, …) wins even if max is somehow negative, so the result is
  // never below 0.
  return Math.max(0, Math.min(max, current + delta))
}

export interface ColumnTrimSpan {
  /** Index (in the original sentence array) of the first visible sentence. */
  firstIdx: number
  /** Index of the last visible sentence. */
  lastIdx: number
  /** Words trimmed off the start of the first visible sentence. */
  firstLeadTrim: number
  /** Words trimmed off the end of the last visible sentence. */
  lastTailTrim: number
}

/**
 * Resolve a column's word-level skip/cut into which sentences are visible and how
 * much the first/last visible one is trimmed. skip/cut cascade across verses —
 * skip hides whole leading verses then a partial one, cut does the trailing end —
 * so "remove a word" works past a verse boundary. Always keeps >= 1 word visible
 * (the cap lives here, so an over-large stored skip/cut can never blank the column).
 */
export function resolveColumnTrim(wordCounts: number[], skip: number, cut: number): ColumnTrimSpan {
  const n = wordCounts.length
  const total = wordCounts.reduce((sum, c) => sum + c, 0)
  const maxRemovable = Math.max(0, total - 1)
  let s = Math.max(0, skip)
  let c = Math.max(0, cut)
  if (s > maxRemovable) {
    s = maxRemovable
  }
  if (s + c > maxRemovable) {
    c = maxRemovable - s
  }

  let firstIdx = 0
  let lead = s
  while (firstIdx < n - 1 && lead >= wordCounts[firstIdx]) {
    lead -= wordCounts[firstIdx]
    firstIdx++
  }

  let lastIdx = n - 1
  let tail = c
  while (lastIdx > 0 && tail >= wordCounts[lastIdx]) {
    tail -= wordCounts[lastIdx]
    lastIdx--
  }

  // The >= 1-word cap keeps firstIdx <= lastIdx; clamp defensively just in case.
  if (firstIdx > lastIdx) {
    lastIdx = firstIdx
    tail = 0
    lead = Math.min(lead, Math.max(0, wordCounts[firstIdx] - 1))
  }
  return { firstIdx, firstLeadTrim: lead, lastIdx, lastTailTrim: tail }
}
